export enum MarketAreaStatus {
  ACTIVE = "ACTIVE",
  ARCHIVED = "DISABLED",
}

export enum MarketAreaType {
  POLYGON = "POLYGON",
  RADIUS = "RADIUS",
  DRIVE_TIME = "DRIVE_TIME",
  OTHER = "OTHER",
}

export enum StandardGeographyType {
  NATIONAL = "NATIONAL",
  STATE = "STATE",
  MSA = "MSA",
  COUNTY = "COUNTY",
  TOWNSHIP_BOROUGH = "TOWNSHIP_BOROUGH",
  CITY = "CITY",
  ZIP = "ZIP",
  CENSUS_TRACT = "CENSUS_TRACT",
  CENSUS_BLOCK_GROUP = "CENSUS_BLOCK_GROUP",
}

export interface IStandardGeographyDto {
  layer: string;
  id: string;
  name: string;
  stateAbbr: string | null;
  rings: number[][][];
}

export interface IStandardGeographySelection {
  areaId: string;
  name: string;
  stateAbbr: string | null;
  rings: ICoordinate[][];
}

export enum MarketAreaAnalysisStatus {
  NOT_ANALYZED = "NOT_ANALYZED",
  IN_PROGRESS = "ANALYZING_IN_PROGRESS",
  ANALYZED = "ANALYZED",
  FAILED = "FAILED_DURING_ANALYSIS",
}

export interface ICoordinate {
  latitude: number;
  longitude: number;
}

export interface IPolygonGeometry {
  type: string;
  vertices: ICoordinate[];
}

export interface IRadiusGeometry {
  type: string;
  center: ICoordinate;
  radiusMiles: number;
}

export interface IDriveTimeGeometry {
  type: MarketAreaType.DRIVE_TIME;
  center: ICoordinate;
  driveTimeMinutes: number;
  travelMode: string;
  rings: ICoordinate[][];
}

export interface IAdministrativeGeographyGeometry {
  type: MarketAreaType.OTHER;
  geographyType: StandardGeographyType;
  sourceCountry: "US";
  layer: string;
  selections: IStandardGeographySelection[];
}

export type TMarketAreaGeometry =
  | IPolygonGeometry
  | IRadiusGeometry
  | IDriveTimeGeometry
  | IAdministrativeGeographyGeometry;

export interface IMarketAreaAnalysisError {
  code: string;
  message: string;
}

export interface IAssignedProperty {
  id: string;
  latitude: number;
  longitude: number;
}

export interface IAssignedProperties {
  asPrimary: IAssignedProperty[];
  asSecondary: IAssignedProperty[];
}

interface IMarketAreaBase {
  id: string;
  name: string;
  description: string | null;
  analysisStatus: MarketAreaAnalysisStatus;
  status: MarketAreaStatus;
  createdBy: string;
  creationDate: string;
  lastAnalysisDate: string | null;
  analysisError: IMarketAreaAnalysisError | null;
  propertyCount?: number;
  assignedProperties?: IAssignedProperties;
}

export type TMarketArea =
  | (IMarketAreaBase & {
      type: MarketAreaType.POLYGON;
      geometry: IPolygonGeometry;
    })
  | (IMarketAreaBase & {
      type: MarketAreaType.RADIUS;
      geometry: IRadiusGeometry;
    })
  | (IMarketAreaBase & {
      type: MarketAreaType.DRIVE_TIME;
      geometry: IDriveTimeGeometry;
    })
  | (IMarketAreaBase & {
      type: MarketAreaType.OTHER;
      geometry: IAdministrativeGeographyGeometry;
    });

export interface IMarketAreasInfo {
  totalCount: number;
  activeCount: number;
  objects: TMarketArea[];
}

export enum MarketAreaPriorityEnum {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
}

export interface IMarketAreaTierLimit {
  enabled: boolean;
}

export interface IMarketAreaLimits {
  basic: IMarketAreaTierLimit;
  pro: IMarketAreaTierLimit;
  team: IMarketAreaTierLimit;
}

export interface IMarketAreaField {
  id: string;
  name: string | null;
}

export interface IMarketAreaSettings {
  limits: IMarketAreaLimits;
  selectedFields: IMarketAreaField[];
  favoriteFields: IMarketAreaField[];
}
